import {
  ApplicationRef,
  createComponent,
  EventEmitter,
  inject,
  Injectable,
  inputBinding,
  NgZone
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { nanoid } from "nanoid";
// @ts-ignore
import { el } from "src/app/support/dom";
import { from, lastValueFrom, Subscription } from "rxjs";
import { ProgressBarComponent } from "src/app/components/ui/progress-bar/progress-bar.component";
import { sntTranslate } from "src/app/support/translate";
import Swal from "sweetalert2";

export type TaskRef = {
  id: string;
  type: TaskType;
  fn: () => void | Promise<void>;
  interval?: number;
  intervalId?: number;
  timeoutId?: number;
};

export enum TaskType {
  RUN_ONCE = "run-once",
  RUN_ONCE_ASYNC = "run-once-async",
  RECURRING = "recurring"
}

export enum TaskExecutionPlan {
  WHEN_STABLE = "when-stable",
  WHEN_USER_READY = "when-user-ready",
  IMMEDIATELY = "immediately"
}

export type TaskExecuteInput = {
  execFn: (tracker: EventEmitter<any>, list?: Array<any>) => Promise<void>;
  title: string;
  subTitle?: string;
  list?: Array<any>;
};

export type TaskScheduleInput = {
  type: TaskType;
  plan: TaskExecutionPlan;
  fn: () => void;
  interval?: number;
};

@Injectable()
export class TasksService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly ngZone = inject(NgZone);

  #userReadyTasks: TaskRef[] = [];
  private whenStableTasks: TaskRef[] = [];

  private isStable: boolean = false;

  constructor() {
    this.applicationRef.isStable.subscribe((isStable: boolean) => {
      if (isStable) {
        if (!this.isStable) {
          this.isStable = true;

          console.log('[TasksService] "isStable" is true');
        }

        this.whenStableTasks.forEach((ref) => {
          this.runTask(ref);
        });

        this.whenStableTasks = [];
      }
    });
  }

  userReady(): void {
    this.#userReadyTasks.forEach((ref: TaskRef) => {
      this.runTask(ref);
    });
  }

  async execute({
    execFn,
    title,
    subTitle,
    list
  }: TaskExecuteInput): Promise<void> {
    const ref: TaskRef = {
      id: nanoid(),
      type: TaskType.RUN_ONCE_ASYNC,
      fn: async () => {
        let tracker = new EventEmitter<number>();
        let sub: Subscription;

        const hostElement = el("div");

        const componentRef = createComponent(ProgressBarComponent, {
          environmentInjector: this.applicationRef.injector,
          hostElement,
          bindings: [
            inputBinding("title", () => title),
            inputBinding("subTitle", () => subTitle),
            inputBinding("allSteps", () => 1),
            inputBinding("onAllStepsCompleted", () => Swal.close)
          ]
        });

        componentRef.changeDetectorRef.detach();
        componentRef.changeDetectorRef.detectChanges();

        await lastValueFrom(
          from(
            new Promise<void>((res) => {
              Swal.fire({
                html: hostElement,
                showCancelButton: false,
                showConfirmButton: false,
                toast: true,
                position: "bottom-left",
                showClass: {
                  backdrop: "swal2-noanimation overflow-visible",
                  popup: "",
                  icon: ""
                },
                hideClass: {
                  popup: ""
                },
                customClass: {
                  popup: ["mb-6", "ml-14"]
                },
                didOpen: async () => {
                  try {
                    sub = tracker.subscribe((step) => {
                      componentRef.instance.increase.emit(step);
                      componentRef.changeDetectorRef.detectChanges();
                    });

                    await execFn(tracker, list);
                  } catch (ex) {
                    console.error(ex);
                    Swal.fire({
                      title: sntTranslate(marker("Errore")),
                      toast: true,
                      position: "bottom-left",
                      timer: 3000
                    });
                  } finally {
                    sub.unsubscribe();
                    res();
                  }
                }
              });
            })
          )
        );
      },
      interval: undefined,
      intervalId: undefined,
      timeoutId: undefined
    };

    if (this.isStable) {
      this.runTask(ref);
    }
  }

  schedule(input: TaskScheduleInput): TaskRef {
    const ref: TaskRef = {
      id: nanoid(),
      type: input.type,
      fn: input.fn,
      interval: input.interval,
      intervalId: undefined,
      timeoutId: undefined
    };

    switch (input.plan) {
      case TaskExecutionPlan.WHEN_USER_READY: {
        this.#userReadyTasks.push(ref);
        break;
      }
      case TaskExecutionPlan.WHEN_STABLE: {
        if (this.isStable) {
          this.runTask(ref);
          break;
        } else {
          this.whenStableTasks.push(ref);
        }

        break;
      }
      case TaskExecutionPlan.IMMEDIATELY: {
        this.runTask(ref);
        break;
      }
    }

    return ref;
  }

  private runTask(ref: TaskRef): void {
    switch (ref.type) {
      case TaskType.RUN_ONCE: {
        this.ngZone.runOutsideAngular(() => {
          ref.timeoutId = window.setTimeout(() => ref.fn(), ref.interval || 0);
        });

        break;
      }
      case TaskType.RUN_ONCE_ASYNC: {
        this.ngZone.runOutsideAngular(() => {
          ref.timeoutId = window.setTimeout(async () => {
            await ref.fn();
          }, ref.interval || 0);
        });

        break;
      }
      case TaskType.RECURRING: {
        this.ngZone.runOutsideAngular(() => {
          ref.intervalId = window.setInterval(() => {
            ref.fn();
          }, ref.interval);
        });

        break;
      }
    }
  }

  delete(ref: TaskRef): void {
    this.whenStableTasks = this.whenStableTasks.filter((r) => r.id !== ref.id);

    if (ref.timeoutId) {
      window.clearTimeout(ref.timeoutId);
    }

    if (ref.intervalId) {
      window.clearInterval(ref.intervalId);
    }
  }
}
